import React, { useEffect, useRef, useState } from 'react';
import styles from './HTMLViewer.module.scss';
import SearchToolbar from './SearchToolbar';

interface HTMLViewerProps {
  directUrl: string;
  documentId: string;
  pageId: string | null;
  textToHighlight: string | null;
  hasEmbeddings: boolean;
}

const HTMLViewer: React.FC<HTMLViewerProps> = ({
  directUrl,
  documentId,
  pageId,
  textToHighlight,
  hasEmbeddings = true,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [searchPageId, setSearchPageId] = useState<string | null>(null);
  const [searchHighlightText, setSearchHighlightText] = useState<string | null>(null);

  // Extract the first few words from a text string
  const extractFirstWords = (text: string, wordCount: number): string => {
    if (!text) return '';
    const firstLine = text.split('\n')[0];
    const words = firstLine.trim().split(/\s+/);
    return words.slice(0, wordCount).join(' ');
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(directUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch HTML content: ${response.statusText}`);
        }
        const htmlContent = await response.text();
        setContent(htmlContent);
        setError(null);
      } catch (err) {
        console.error('Error fetching HTML content:', err);
        setError('Failed to load document content');
      }
    };

    fetchContent();
  }, [directUrl]);

  // Effect for handling content, highlighting, and scrolling
  useEffect(() => {
    if (!content || !iframeRef.current) return;

    const iframe = iframeRef.current;
    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iframeDoc) return;

    // Extract the body content from the original HTML
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : content;

    // Write the content to the iframe with proper formatting
    const contentToWrite = `
      <!DOCTYPE html>
      <html>
        <head>
          <style>
            .highlight-text { background-color: yellow; }
            body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
            h1 { margin-bottom: 5px; }
            .date { color: #666; font-size: 0.9em; margin-bottom: 20px; }
            hr { margin: 20px 0; border: none; border-top: 1px solid #ddd; }
            h2 { margin-bottom: 0; color: #333; }
            .title { color: #666; font-size: 0.7em; margin-bottom: 10px; font-weight: normal; display: block; margin-top: 4px; }
            .text { margin-bottom: 15px; line-height: 1.5; }
            .text:last-child { margin-bottom: 30px; }
          </style>
        </head>
        <body>
          ${bodyContent}
        </body>
      </html>
    `;

    // Write the content to the iframe
    iframeDoc.open();
    iframeDoc.write(contentToWrite);
    iframeDoc.close();

    // Add load event listener to ensure content is fully loaded
    const handleLoad = () => {
      if (!iframeDoc.body) {
        return;
      }
      setDocumentLoaded(true);

      // Handle text highlighting if needed
      const highlightCandidate = searchHighlightText || textToHighlight;
      const pageCandidate = searchPageId || pageId;
      if (highlightCandidate && iframeDoc.body) {
        const processedTextToHighlight = extractFirstWords(highlightCandidate, 5);

        // Find the target element first
        const targetElement = pageCandidate ? iframeDoc.getElementById(pageCandidate) : null;

        // Function to get the position of a node in the document
        const getNodePosition = (node: Node): number => {
          const range = iframeDoc.createRange();
          range.selectNode(node);
          return range.getBoundingClientRect().top;
        };

        let bestMatch: { node: Node; distance: number } | null = null;
        const targetPosition = targetElement ? getNodePosition(targetElement) : 0;

        const highlightText = (node: Node): boolean => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || '';
            const index = text.toLowerCase().indexOf(processedTextToHighlight.toLowerCase());

            if (index >= 0) {
              const currentPosition = getNodePosition(node);
              const distance = currentPosition - targetPosition;

              // Only consider matches that come after the target element
              if (distance >= 0 && (!bestMatch || distance < bestMatch.distance)) {
                bestMatch = { node, distance };
              }
            }
          }
          return false;
        };

        const traverse = (node: Node): boolean => {
          if (highlightText(node)) return true;
          const children = Array.from(node.childNodes);
          for (const child of children) {
            if (traverse(child)) return true;
          }
          return false;
        };

        // First pass to find the best match
        traverse(iframeDoc.body);

        // If we found a match, highlight it
        if (bestMatch) {
          const matchNode = (bestMatch as { node: Node; distance: number }).node;
          const text = matchNode.textContent || '';
          const index = text.toLowerCase().indexOf(processedTextToHighlight.toLowerCase());

          const before = text.substring(0, index);
          const highlight = text.substring(index, index + processedTextToHighlight.length);
          const after = text.substring(index + processedTextToHighlight.length);

          const fragment = iframeDoc.createDocumentFragment();
          if (before) fragment.appendChild(iframeDoc.createTextNode(before));

          const span = iframeDoc.createElement('span');
          span.className = 'highlight-text';
          span.textContent = highlight;
          fragment.appendChild(span);

          if (after) fragment.appendChild(iframeDoc.createTextNode(after));

          matchNode.parentNode?.replaceChild(fragment, matchNode);

          // Scroll to the highlighted text
          setTimeout(() => {
            span.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        } else if (targetElement) {
          // If no match was found but we have a pageId, scroll to that element instead
          setTimeout(() => {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    };

    // Add load event listener to iframe
    iframe.addEventListener('load', handleLoad);

    // Cleanup
    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [content, textToHighlight, pageId, documentLoaded, searchHighlightText, searchPageId]);

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {hasEmbeddings && (
        <SearchToolbar
          documentId={documentId}
          onNavigate={({ pageId: pid, highlightText }) => {
            setSearchPageId(pid);
            setSearchHighlightText(highlightText || null);
          }}
        />
      )}
      {!content ? (
        <div className={styles.loading}>Loading document...</div>
      ) : (
        <iframe
          ref={iframeRef}
          className={styles.htmlViewer}
          title="Document Viewer"
          sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
        />
      )}
    </div>
  );
};

export default HTMLViewer;
